import type { User } from '../types/auth';
import type { Tender } from '../types/tender';
import { useStructureCsv } from './useProposalStructure/useStructureCsv';
import { useStructureExtracted } from './useProposalStructure/useStructureExtracted';
import { useStructureMutations } from './useProposalStructure/useStructureMutations';
import { useStructureQuickSettings } from './useProposalStructure/useStructureQuickSettings';
import { useStructureState } from './useProposalStructure/useStructureState';
import { useStructureTemplates } from './useProposalStructure/useStructureTemplates';

export interface UseProposalStructureProps {
  tenderId: string | undefined;
  tender: Tender | null;
  account: (User & { clientId?: string; aiSettings?: unknown }) | null;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
}

export const useProposalStructure = ({
  tender,
  account,
  showSnackbar,
}: UseProposalStructureProps) => {
  const { proposalStructure, setProposalStructure, sensors } = useStructureState(
    tender,
    account,
    showSnackbar
  );

  const { availableTemplates, selectedTemplates, handleTemplateChange } = useStructureTemplates(
    tender,
    account,
    showSnackbar
  );

  const { quickSettings, handleQuickSettingsChange } = useStructureQuickSettings(
    tender,
    account,
    showSnackbar
  );

  const { handleDragEnd, handleHeadingUpdate, handleFocuserUpdate, removeHeading } =
    useStructureMutations(setProposalStructure);

  const { isUploadingStructure, handleStructureFileUpload, downloadStructureTemplate } =
    useStructureCsv(proposalStructure, setProposalStructure, tender, showSnackbar);

  const { handleLoadExtractedStructure } = useStructureExtracted(
    setProposalStructure,
    tender,
    quickSettings,
    showSnackbar
  );

  return {
    proposalStructure,
    isUploadingStructure,
    availableTemplates,
    selectedTemplates,
    quickSettings,
    sensors,
    setProposalStructure,
    handleDragEnd,
    handleHeadingUpdate,
    handleFocuserUpdate,
    removeHeading,
    handleStructureFileUpload,
    handleLoadExtractedStructure,
    downloadStructureTemplate,
    handleTemplateChange,
    handleQuickSettingsChange,
  };
};
